import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faEnvelope,
  faPhoneAlt,
  faCommentAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook,
  faInstagram,
  faTwitter,
  faTiktok,
} from '@fortawesome/free-brands-svg-icons';
import { LanguageContext } from '../LanguageContext';
import contactBackground from '../img/unnamed.jpg';

function Contact() {
  const { language } = useContext(LanguageContext);

  const content = {
    fr: {
      pageTitle: 'NOUS CONTACTER',
      subtitle: 'Nous sommes à votre disposition pour répondre à toutes vos questions',
      contactInfo: {
        location: {
          title: 'Localisation',
          address1: 'Uniquement sur Rendez-vous :',
          address2: '3 Rue du Port',
          address3: '1204 Genève - CH',
        },
        email: {
          title: 'Adresse e-mail',
          address: 'Doyoufarahman@gmail.com',
        },
        phone: {
          title: 'Téléphone',
          number: '+41 79 924 74 24',
        },
        social: {
          title: 'Réseaux sociaux',
          followText: 'Suivez-nous sur',
        },
      },
    },
    ar: {
      pageTitle: 'اتصل بنا',
      subtitle: 'نحن في خدمتكم للإجابة على جميع استفساراتكم',
      contactInfo: {
        location: {
          title: 'الموقع',
          address1: 'حسب الموعد فقط:',
          address2: '3 Rue du Port',
          address3: '1204 جنيف - سويسرا',
        },
        email: {
          title: 'البريد الإلكتروني',
          address: 'info@doyoufarahman.com',
        },
        phone: {
          title: 'الهاتف',
          number: '+41 79 924 74 24',
        },
        social: {
          title: 'وسائل التواصل الاجتماعي',
          followText: 'تابعونا على',
        },
      },
    },
  };

  const textAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const cardAnimation = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
    hover: {
      y: -5,
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
    },
  };

  return (
    <section
      className="bg-gradient-to-b from-blue-50 to-gray-50"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      
      <div className="relative h-[500px] overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 h-full"
        >
          <img
            className="w-full h-full object-cover"
            src={contactBackground}
            alt={language === 'fr' ? 'Contact Background' : 'خلفية الاتصال'}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80"></div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={textAnimation}
          className="relative h-full flex flex-col items-center justify-center px-4 text-center"
        >
          <h1 className="text-white text-5xl md:text-6xl font-extrabold mb-4 tracking-wide">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-300 to-green-500">
              {content[language].pageTitle}
            </span>
          </h1>
          <div className="w-32 h-2 bg-green-400 rounded-full mb-6"></div>
          <p className="text-gray-200 text-xl max-w-2xl">{content[language].subtitle}</p>
        </motion.div>
      </div>

     
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={textAnimation}
          className="mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
           
           
            <motion.div
              custom={0}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true }}
              variants={cardAnimation}
              className="bg-white p-8 rounded-xl shadow-md text-center flex flex-col items-center transition-all duration-300"
            >
              <div className="bg-green-100 p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-2xl text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {content[language].contactInfo.location.title}
              </h3>
              <div className="space-y-2 text-gray-600">
                <p>{content[language].contactInfo.location.address1}</p>
                <p className="font-medium">{content[language].contactInfo.location.address2}</p>
                <p>{content[language].contactInfo.location.address3}</p>
              </div>
            </motion.div>

           
            <motion.div
              custom={1}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true }}
              variants={cardAnimation}
              className="bg-white p-8 rounded-xl shadow-md text-center flex flex-col items-center transition-all duration-300"
            >
              <div className="bg-green-100 p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faEnvelope} className="text-2xl text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {content[language].contactInfo.email.title}
              </h3>
              <a
                href={`mailto:${content[language].contactInfo.email.address}`}
                className="text-green-600 hover:text-green-800 transition-colors duration-300"
              >
                {content[language].contactInfo.email.address}
              </a>
            </motion.div>

            <motion.div
              custom={2}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true }}
              variants={cardAnimation}
              className="bg-white p-8 rounded-xl shadow-md text-center flex flex-col items-center transition-all duration-300"
            >
              <div className="bg-green-100 p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faPhoneAlt} className="text-2xl text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {content[language].contactInfo.phone.title}
              </h3>
              <a
                href={`tel:${content[language].contactInfo.phone.number.replace(/\s/g, '')}`}
                className="text-green-600 hover:text-green-800 transition-colors duration-300 text-lg"
              >
                {content[language].contactInfo.phone.number}
              </a>
            </motion.div>

            <motion.div
              custom={3}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true }}
              variants={cardAnimation}
              className="bg-white p-8 rounded-xl shadow-md text-center flex flex-col items-center transition-all duration-300"
            >
              <div className="bg-green-100 p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faCommentAlt} className="text-2xl text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {content[language].contactInfo.social.title}
              </h3>
              <p className="text-gray-600 mb-4">{content[language].contactInfo.social.followText}</p>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/doyoufarahman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-100 p-3 rounded-full text-gray-700 hover:bg-green-500 hover:text-white transition-colors duration-300"
                >
                  <FontAwesomeIcon icon={faFacebook} className="text-xl" />
                </a>
                <a
                  href="https://www.instagram.com/doyoufarahman1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-100 p-3 rounded-full text-gray-700 hover:bg-pink-500 hover:text-white transition-colors duration-300"
                >
                  <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                </a>
                <a
                  href="https://www.tiktok.com/@doyouf.arahman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-100 p-3 rounded-full text-gray-700 hover:bg-black hover:text-white transition-colors duration-300"
                >
                  <FontAwesomeIcon icon={faTiktok} className="text-xl" />
                </a>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={textAnimation}
          className="mt-16"
        >
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <iframe
              title="Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2761.356835477968!2d6.140341315583913!3d46.20499257911689!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c6521de7a1a19%3A0x4834e4c8654c7e3b!2sRue%20du%20Port%203%2C%201204%20Gen%C3%A8ve%2C%20Switzerland!5e0!3m2!1sen!2s!4v1620000000000!5m2!1sen!2s"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Contact;
